import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Box,
  Typography,
  Divider,
  Stepper,
  Step,
  StepLabel,
  TextField,
  MenuItem,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  AutoAwesome,
  Save,
  Send,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { brand, border as borderColors, text as textColors } from '../../theme/colors';
import DialogCloseButton from '../common/DialogCloseButton';
import contractService from '../../services/contractService';
import {
  CONTRACT_SECTION_ORDER,
  sectionLabel,
  sectionParts,
} from '../../utils/contractSections';

/**
 * AIContractGenerator
 *
 * A compact 3-step wizard for a contractor to turn an accepted quotation into a
 * formal contract: (1) commercial terms, (2) AI draft review + manual editing,
 * (3) preview + save/send. Mirrors AIBidGenerator's dialog + stepper styling.
 *
 * Props:
 *  - open, onClose
 *  - bid: the accepted bid this contract is for
 *  - existingContract: optional draft contract to resume
 *  - onChanged: called after a successful save/send so parents can refresh
 */
const STEPS = ['Commercial terms', 'Review draft', 'Preview & send'];

const DEFAULT_VARIATION_TEXT =
  'Any change to the scope, materials, or quantities is agreed in writing through an Eqaya change order, with the cost and time impact confirmed by both parties, before the additional work begins.';

// Seed the editor with the canonical sections (empty) so a contractor can write
// manually even if AI generation is skipped or fails.
const emptySections = () =>
  CONTRACT_SECTION_ORDER.reduce((acc, key) => {
    acc[key] = '';
    return acc;
  }, {});

// Flatten a persisted body (values may be strings or { title, text }) into the
// editor's flat { key: string } shape.
const sectionsFromExisting = (body) => {
  const seeded = emptySections();
  if (body && typeof body === 'object') {
    Object.keys(body).forEach((key) => {
      seeded[key] = sectionParts(key, body[key]).text;
    });
  }
  return seeded;
};

const AIContractGenerator = ({ open, onClose, bid, existingContract, onChanged }) => {
  const { user } = useAuth();

  const [activeStep, setActiveStep] = useState(existingContract ? 1 : 0);

  // Step 1 — commercial terms
  const [retentionPercent, setRetentionPercent] = useState(5);
  const [defectsLiabilityMonths, setDefectsLiabilityMonths] = useState(3);
  const [materialsResponsibility, setMaterialsResponsibility] = useState('contractor_supplied');
  const [insuranceResponsibility, setInsuranceResponsibility] = useState('contractor');
  const [variationProcedure, setVariationProcedure] = useState(DEFAULT_VARIATION_TEXT);
  const [siteAccessNotes, setSiteAccessNotes] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');

  // Step 2 — draft body (flat { key: string }) + AI metadata
  const [sections, setSections] = useState(() =>
    existingContract ? sectionsFromExisting(existingContract.body) : emptySections()
  );
  const [aiGenerated, setAiGenerated] = useState(!!existingContract?.ai_generated);
  const [aiModel, setAiModel] = useState(null);
  const [aiTokensUsed, setAiTokensUsed] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [aiError, setAiError] = useState('');
  const [hasGenerated, setHasGenerated] = useState(!!existingContract);

  // Persistence
  const [contractId, setContractId] = useState(existingContract?.id || null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const busy = generating || saving || sending;

  const showMessage = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const signerName =
    `${user?.profile?.firstName || user?.first_name || ''} ${user?.profile?.lastName || user?.last_name || ''}`.trim() ||
    user?.company_name ||
    user?.email ||
    'you';

  const commercialTerms = useMemo(
    () => ({
      retentionPercent: Number(retentionPercent) || 0,
      defectsLiabilityMonths: Number(defectsLiabilityMonths) || 0,
      variationProcedure,
      siteAccessNotes,
      insuranceResponsibility,
      materialsResponsibility,
      specialInstructions,
    }),
    [
      retentionPercent,
      defectsLiabilityMonths,
      variationProcedure,
      siteAccessNotes,
      insuranceResponsibility,
      materialsResponsibility,
      specialInstructions,
    ]
  );

  // Ordered keys for editing/preview: seeded canonical order plus any AI extras.
  const orderedKeys = useMemo(() => {
    const keys = Object.keys(sections);
    const known = CONTRACT_SECTION_ORDER.filter((k) => keys.includes(k));
    const extra = keys.filter((k) => !CONTRACT_SECTION_ORDER.includes(k));
    return [...known, ...extra];
  }, [sections]);

  const buildBodyPayload = () => {
    const body = {};
    orderedKeys.forEach((key) => {
      const value = (sections[key] || '').trim();
      if (value) body[key] = value;
    });
    return body;
  };

  const handleGenerate = async () => {
    if (!bid?.id) return;
    setGenerating(true);
    setAiError('');
    try {
      const result = await contractService.generate(bid.id, commercialTerms);
      const generatedBody = result?.body || {};
      const next = emptySections();
      Object.keys(generatedBody).forEach((key) => {
        next[key] = sectionParts(key, generatedBody[key]).text;
      });
      setSections(next);
      setAiGenerated(true);
      setAiModel(result?.metadata?.model || null);
      setAiTokensUsed(result?.metadata?.tokensUsed ?? result?.metadata?.ai_tokens_used ?? null);
      setHasGenerated(true);
      showMessage('AI draft ready — review and edit each section.', 'success');
    } catch (error) {
      console.error('Contract AI generation failed:', error);
      setAiError(
        error.response?.data?.error ||
          'AI could not draft this contract right now. You can still write each section manually below.'
      );
      setHasGenerated(true);
    } finally {
      setGenerating(false);
    }
  };

  const handleSectionChange = (key, value) =>
    setSections((prev) => ({ ...prev, [key]: value }));

  // Create the draft on first save, then update on subsequent saves. Returns the
  // persisted contract (with its id).
  const persistDraft = async () => {
    const body = buildBodyPayload();
    if (Object.keys(body).length === 0) {
      throw new Error('Add at least one section before saving.');
    }
    if (contractId) {
      return contractService.updateBody(contractId, body);
    }
    const created = await contractService.create({
      bidId: bid.id,
      body,
      aiGenerated,
      aiModel,
      aiTokensUsed,
    });
    if (created?.id) setContractId(created.id);
    return created;
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await persistDraft();
      showMessage('Contract draft saved.', 'success');
      if (onChanged) onChanged();
    } catch (error) {
      console.error('Save contract draft failed:', error);
      showMessage(error.response?.data?.error || error.message || 'Failed to save the draft.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmSend = async () => {
    setSending(true);
    try {
      const contract = await persistDraft();
      const id = contract?.id || contractId;
      await contractService.send(id);
      setConfirmSendOpen(false);
      showMessage('Contract sent to client for signature.', 'success');
      if (onChanged) onChanged();
      setTimeout(() => handleClose(), 800);
    } catch (error) {
      console.error('Send contract failed:', error);
      setConfirmSendOpen(false);
      showMessage(error.response?.data?.error || error.message || 'Failed to send the contract.', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleNext = () => setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  const handleBack = () => setActiveStep((prev) => Math.max(prev - 1, 0));

  const currency = bid?.currency || 'USD';
  const totalCost = parseFloat(bid?.total_cost || 0).toLocaleString();
  const duration = bid?.estimated_duration
    ? `${bid.estimated_duration} ${bid.duration_unit || 'weeks'}`
    : '—';

  const hasAnyContent = orderedKeys.some((key) => (sections[key] || '').trim());

  const renderQuoteSummary = () => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'grey.50',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {bid?.project_name || 'Untitled project'}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4}>
          <Typography variant="caption" color="text.secondary">Contract value</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{currency} {totalCost}</Typography>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Typography variant="caption" color="text.secondary">Duration</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{duration}</Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="caption" color="text.secondary">Quotation</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{bid?.bid_number || bid?.id}</Typography>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderCommercialTerms = () => (
    <Box>
      {renderQuoteSummary()}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Answer a few questions below and AI will write the full contract for you, using your accepted quotation. You review and edit every clause before anything is sent to the client.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Retention — money held back (%)"
            helperText="The client keeps back this share of each payment until the whole job is finished and approved. It is paid to you at the end. 5% is common."
            value={retentionPercent}
            onChange={(e) => setRetentionPercent(e.target.value)}
            inputProps={{ min: 0, max: 100 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Free repair period (months)"
            helperText="After you finish, you agree to come back and fix any faults in your work for free during this period (the 'defects liability period'). 3 months is common."
            value={defectsLiabilityMonths}
            onChange={(e) => setDefectsLiabilityMonths(e.target.value)}
            inputProps={{ min: 0 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Who buys the materials?"
            helperText="Who pays for and supplies cement, bricks, fittings and other materials."
            value={materialsResponsibility}
            onChange={(e) => setMaterialsResponsibility(e.target.value)}
          >
            <MenuItem value="contractor_supplied">I supply the materials</MenuItem>
            <MenuItem value="client_supplied">The client supplies the materials</MenuItem>
            <MenuItem value="mixed">We each supply some</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Who carries the insurance?"
            helperText="Who is responsible for insuring the works, tools and people on site."
            value={insuranceResponsibility}
            onChange={(e) => setInsuranceResponsibility(e.target.value)}
          >
            <MenuItem value="contractor">Me (the contractor)</MenuItem>
            <MenuItem value="client">The client</MenuItem>
            <MenuItem value="shared">Shared between us</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="How are changes to the work handled?"
            helperText="What happens if the client asks for extra work or changes the plan mid-job — e.g. agreed in writing with a price before the work is done."
            value={variationProcedure}
            onChange={(e) => setVariationProcedure(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Working on site"
            placeholder="Working hours, access arrangements, storage, security…"
            helperText="Anything about when and how you'll work at the property."
            value={siteAccessNotes}
            onChange={(e) => setSiteAccessNotes(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Special instructions"
            placeholder="Anything specific to this job"
            value={specialInstructions}
            onChange={(e) => setSpecialInstructions(e.target.value)}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderReviewDraft = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          {hasGenerated
            ? 'Edit any section below. Prices come from the accepted quotation.'
            : 'You don\'t have to write this yourself — AI can write the full contract from your accepted quotation and the terms you just chose. You review and edit every section before sending.'}
        </Typography>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={busy}
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
          sx={{ background: brand.gradient, '&:hover': { background: brand.gradientHover }, whiteSpace: 'nowrap' }}
        >
          {generating ? 'Writing contract…' : hasGenerated ? 'Rewrite with AI' : 'Write my contract with AI'}
        </Button>
      </Box>

      {aiError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAiError('')}>
          {aiError}
        </Alert>
      )}

      {orderedKeys.map((key) => (
        <Box key={key} sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {sectionLabel(key)}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={sections[key] || ''}
            onChange={(e) => handleSectionChange(key, e.target.value)}
            placeholder={`Write the ${sectionLabel(key).toLowerCase()} clause…`}
          />
        </Box>
      ))}
    </Box>
  );

  const renderPreview = () => {
    const previewKeys = orderedKeys.filter((key) => (sections[key] || '').trim());
    return (
      <Box>
        {previewKeys.length === 0 ? (
          <Alert severity="info">
            No content yet. Go back to add or generate at least one section.
          </Alert>
        ) : (
          <Paper
            elevation={0}
            sx={{ p: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
          >
            {renderQuoteSummary()}
            {previewKeys.map((key) => (
              <Box key={key} sx={{ mb: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: textColors.primary }}>
                  {sectionLabel(key)}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: textColors.secondary }}>
                  {sections[key]}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}
      </Box>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogCloseButton onClick={handleClose} />
        <DialogTitle sx={{ background: brand.gradient, color: 'white', pr: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon />
            <Box>
              <Typography variant="h6" component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Create contract
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {bid?.project_name || 'Accepted quotation'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              mb: 3,
              '& .MuiStepLabel-root .Mui-active': { color: brand.primary },
              '& .MuiStepLabel-root .Mui-completed': { color: brand.primary },
              '& .MuiStepIcon-root.Mui-active': { color: brand.primary },
              '& .MuiStepIcon-root.Mui-completed': { color: brand.primary },
            }}
          >
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && renderCommercialTerms()}
          {activeStep === 1 && renderReviewDraft()}
          {activeStep === 2 && renderPreview()}
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            p: 2,
            justifyContent: 'space-between',
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: borderColors.light,
          }}
        >
          <Button onClick={handleBack} disabled={activeStep === 0 || busy} startIcon={<ArrowBack />}>
            Back
          </Button>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {activeStep === 2 ? (
              <>
                <Button
                  variant="outlined"
                  onClick={handleSaveDraft}
                  disabled={busy || !hasAnyContent}
                  startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                >
                  {saving ? 'Saving…' : 'Save draft'}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setConfirmSendOpen(true)}
                  disabled={busy || !hasAnyContent}
                  startIcon={<Send />}
                  sx={{ background: brand.gradient, '&:hover': { background: brand.gradientHover } }}
                >
                  Sign & send to client
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForward />}
                disabled={busy}
              >
                Next
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Confirm sign & send */}
      <Dialog open={confirmSendOpen} onClose={() => !sending && setConfirmSendOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Sign &amp; send contract</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your name, <strong>{signerName}</strong>, will be recorded as the contractor's signature and the
            contract will be sent to the client for their signature. You can void it while it is awaiting the client.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSendOpen(false)} disabled={sending}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmSend}
            disabled={sending}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send />}
            sx={{ background: brand.gradient, '&:hover': { background: brand.gradientHover } }}
          >
            {sending ? 'Sending…' : 'Sign & send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AIContractGenerator;
